import { AppPrimaryButton } from '@components/form/button/app-primary-button';
import { AppModal } from '@components/form/Modal/app-modal';
import { AppAlert } from '@components/antd';
import { FieldError } from '@components/field-error';
import { PaymentMethodsList } from '@modules/settings/subscription/components/payment-methods-list';
import { WalletInputNumber } from '@components/form/input-number/wallet-input-number';
import { UsePaymentMethodsListReturn } from '@modules/settings/subscription/hooks/use-payment-methods-list';
import { WALLET_LIMIT } from '@shared/constants';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

export type LoadWalletModalBaseProps = {
  open: boolean;
  onCancel: () => void;
  subtitle: string;
  amount: number;
  onAmountChange: (val: number) => void;
  limitError: string;
  errorAlert?: string;
  paymentMethodsList: UsePaymentMethodsListReturn;
  onConfirm: () => void;
  confirmLoading: boolean;
  confirmLabel: string;
  complianceFooter: ReactNode;
};

export const LoadWalletModalBase = ({
  open,
  onCancel,
  subtitle,
  amount,
  onAmountChange,
  limitError,
  errorAlert,
  paymentMethodsList,
  onConfirm,
  confirmLoading,
  confirmLabel,
  complianceFooter,
}: LoadWalletModalBaseProps) => {
  const { t } = useTranslation();

  return (
    <AppModal
      title={
        <AppModal.Header>
          <AppModal.Title>{capitalize(t('load_wallet'))}</AppModal.Title>
          <AppModal.SubTitle>{subtitle}</AppModal.SubTitle>
        </AppModal.Header>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <div className="space-y-4">
          <AppPrimaryButton className="w-full" onClick={onConfirm} loading={confirmLoading}>
            {confirmLabel}
          </AppPrimaryButton>
          {complianceFooter}
        </div>
      }
      classNames={{ content: 'max-w-[420px]!' }}
    >
      <AppModal.Body>
        <div className="space-y-4 mt-5">
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text-500 uppercase">{t('amount')}</p>
            <WalletInputNumber
              error={!!limitError}
              value={amount}
              step={5}
              onChange={(val) => onAmountChange(val || 0)}
              limit={WALLET_LIMIT.LOAD_WALLET}
            />
            <FieldError message={limitError} />
          </div>

          {errorAlert && <AppAlert showIcon type="error" message={errorAlert} />}

          <PaymentMethodsList
            loading={paymentMethodsList.listSubscriptionResult?.loading}
            paymentCards={paymentMethodsList.paymentCards ?? []}
            paymentPaypal={paymentMethodsList.paymentPaypal ?? []}
            selected={paymentMethodsList.paymentMethod ?? null}
            onChange={(method) => paymentMethodsList.setPaymentMethod(method)}
          />
        </div>
      </AppModal.Body>
    </AppModal>
  );
};
